<template>
    <v-dialog v-model="dialogVisible" persistent max-width="700px">
        <v-card>
            <v-card-title class="modal-header">
                <v-icon size="24" color="#26384B" class="mr-2"> mdi-lock </v-icon>
                <span>{{ t('forms.labels.lockInUseTitle') }}</span>
            </v-card-title>
            <v-card-text class="content">
                <p>
                    {{ t('forms.labels.lockInUse') }}
                </p>
            </v-card-text>
            <v-card-actions class="error-buttons">
                <v-spacer />
                <AQButton color="primary" class="capitalize large" @click="cancel">
                    {{ t('forms.labels.lockInUseLeaveQueue') }}
                </AQButton>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    import { ref, onMounted, onBeforeUnmount } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { getSocketClient } from '@/utilities/socket';
    import { eventBus } from '@/utilities/event-bus';

    interface LockQueueData {
        componentId: string;
        retrieveLock: () => Promise<boolean>;
        retryFunction: () => void;
    }

    interface LockReleasedPayload {
        componentId: string;
    }

    const { t } = useI18n();

    const dialogVisible = ref(false);
    const componentId = ref<string | null>(null);
    const retrieveLock = ref<(() => Promise<boolean>) | null>(null);
    const retryFunction = ref<(() => void) | null>(null);

    const openModal = (...args: unknown[]) => {
        const data = args[0] as LockQueueData;
        componentId.value = data.componentId;
        retrieveLock.value = data.retrieveLock;
        retryFunction.value = data.retryFunction;
        dialogVisible.value = true;
    };

    const onLockReleased = async (...args: unknown[]) => {
        const payload = args[0] as LockReleasedPayload;
        if (payload.componentId === componentId.value && retrieveLock.value) {
            const acquired = await retrieveLock.value();
            if (acquired) {
                dialogVisible.value = false;
                if (retryFunction.value) {
                    retryFunction.value();
                }
                resetModal();
            }
        }
    };

    const cancel = () => {
        dialogVisible.value = false;
        resetModal();
    };

    const resetModal = () => {
        componentId.value = null;
        retrieveLock.value = null;
        retryFunction.value = null;
    };

    // Expose for testing
    defineExpose({
        dialogVisible,
        componentId,
        retrieveLock,
        retryFunction,
        cancel,
        openModal,
        onLockReleased,
        resetModal,
    });

    onMounted(() => {
        eventBus.on('open-lock-queue-modal', openModal);
        const socket = getSocketClient();
        if (socket) {
            socket.on('LOCK_BROADCAST_RELEASED', onLockReleased);
        }
    });

    onBeforeUnmount(() => {
        eventBus.off('open-lock-queue-modal', openModal);
        const socket = getSocketClient();
        if (socket) {
            socket.off('LOCK_BROADCAST_RELEASED', onLockReleased);
        }
    });
</script>
